/**
 * government-config.ts — constants for the Government & Special entity dialog.
 *
 * Source of truth: `docs/port-design/category-cards/government.md`.
 *
 * HEADS UP — this file is a SKELETON per the discovery agent's report. The
 * §16 Government walkthroughs were NOT written in Bill's design doc. The
 * sub-type + level enums below are best-effort defaults per the card:
 *
 *   - Government Level: Federal / State / County / Municipal / School
 *     District / Special District (standard US-municipal classification)
 *   - Entity Sub-type: PublicFunds (primary), Donation, ICS_Personal
 *     (Bill still needs to define Donation + ICS_Personal shapes — see
 *     government.md §Open Questions)
 *
 * Per Bill's 2026-04-23 admin-configurable architecture mandate, these MUST
 * eventually move to SQL catalog tables:
 *   - `GovernmentLevels` lookup catalog
 *   - `GovernmentSubTypes` catalog (value + call class + tin code + gates)
 *
 * For v0.1.x these are hardcoded; port phase 2 moves them behind the
 * `useConfig()` bundle in `/v3/config/*` responses.
 */

// -----------------------------------------------------------------------------
// Government Level (jurisdiction classification)
// -----------------------------------------------------------------------------

export interface GovernmentLevelOption {
  readonly value: string;
  readonly label: string;
  /** Short officer-facing descriptor shown as help text. */
  readonly helpText?: string;
}

export const GOVERNMENT_LEVELS: ReadonlyArray<GovernmentLevelOption> = [
  {
    value: 'Federal',
    label: 'Federal',
    helpText: 'US federal government agency or subdivision.',
  },
  {
    value: 'State',
    label: 'State',
    helpText: 'State government agency or subdivision.',
  },
  {
    value: 'County',
    label: 'County',
    helpText: 'County government — e.g., Phelps County.',
  },
  {
    value: 'Municipal',
    label: 'Municipal',
    helpText: 'City / town government — e.g., City of Rolla.',
  },
  {
    value: 'SchoolDistrict',
    label: 'School District',
    helpText: 'Public school district — e.g., Rolla Public Schools.',
  },
  {
    value: 'SpecialDistrict',
    label: 'Special District',
    helpText: 'Fire / water / sewer / library / ambulance district.',
  },
];

// -----------------------------------------------------------------------------
// Government sub-types (3 per government.md §"Entity sub-types")
// -----------------------------------------------------------------------------

/**
 * Canonical sub-type identifiers used inside the dialog + derivation hook.
 *
 * TODO(bill): government walkthrough needed — Donation + ICS_Personal
 * walkthrough specifics are not yet defined (see government.md §Open
 * Questions #1, #3, #4). PublicFunds is the primary/clearest path per
 * ENTITY-TYPE-PROCEDURE-ANALYSIS.md §19.
 */
export type GovernmentSubType = 'PublicFunds' | 'Donation' | 'ICS_Personal';

export interface GovernmentSubTypeOption {
  readonly value: GovernmentSubType;
  readonly label: string;
  /** Short officer-facing descriptor. */
  readonly helpText: string;
  /** Core Director Call Class (legacy field 23) per government.md §"Entity sub-types". */
  readonly callClass: string;
  /** TIN code default per sub-type. */
  readonly tinCodeDefault: 'S' | 'E';
  /** Consumer flag — false for PublicFunds + ICS_Personal, true for Donation (short-term). */
  readonly consumerFlag: boolean;
  /** Whether CDD exemption auto-applies. */
  readonly cddAutoExempt: boolean;
  /** CDD exempt reason code (Core Director Field 378). */
  readonly cddExemptReasonCode?: string;
  /** When true, surface the Finance-approval gate banner. */
  readonly requiresFinanceApproval: boolean;
  /** When true, dialog renders a red "FSR should not open" blocking banner. */
  readonly financeOnly: boolean;
  /** When true, supervisor approval required before CIF commit. */
  readonly requiresSupervisorApproval: boolean;
  /** Primary-doc docCode for auto-fulfillment. */
  readonly primaryDocCode?: string;
}

export const GOVERNMENT_SUB_TYPES: ReadonlyArray<GovernmentSubTypeOption> = [
  {
    value: 'PublicFunds',
    label: 'Public Funds (Government Entity)',
    helpText:
      'Municipal / county / school district / special district. CDD-exempt ' +
      '(reason 15). Requires Finance approval + bid documentation + Governing ' +
      'Body Resolution.',
    callClass: '22',
    tinCodeDefault: 'E',
    consumerFlag: false,
    cddAutoExempt: true,
    cddExemptReasonCode: '15',
    requiresFinanceApproval: true,
    financeOnly: false,
    requiresSupervisorApproval: false,
    primaryDocCode: 'PUBFUNDS_BID_DOC',
  },
  {
    value: 'Donation',
    label: 'Donation / Fundraiser',
    helpText:
      // TODO(bill): government walkthrough needed — Donation has a split
      // short-term/long-term path per government.md §14. Short-term uses
      // Individual SSN (TinCode=S); long-term is Trust-treated (TinCode=E).
      // A secondary radio should drive the split; pending Bill clarification.
      'Donation or fundraiser account. Short-term uses individual SSN; ' +
      'long-term scholarship path is treated as a Trust. ' +
      'TODO(bill): walkthrough needed to finalize split.',
    callClass: '32',
    // Default to S (short-term); long-term variant flips to E via derivation.
    tinCodeDefault: 'S',
    consumerFlag: true,
    cddAutoExempt: false,
    requiresFinanceApproval: false,
    financeOnly: false,
    requiresSupervisorApproval: false,
    primaryDocCode: 'DONATION_STATEMENT',
  },
  {
    value: 'ICS_Personal',
    label: 'ICS Personal (Finance Dept Only)',
    helpText:
      'IntraFi Cash Sweep Personal — Finance-department-initiated only. ' +
      'FSR should NOT open this via Deposit Pro. Contact Finance for ' +
      'pre-vetted details. Requires Executive Committee approval.',
    callClass: '14',
    tinCodeDefault: 'E',
    consumerFlag: false,
    cddAutoExempt: true,
    requiresFinanceApproval: true,
    financeOnly: true,
    requiresSupervisorApproval: true,
    primaryDocCode: undefined,
  },
];

/** Fast-lookup set — sub-types blocked from FSR opening. */
export const FINANCE_ONLY_SUBTYPES: ReadonlySet<string> = new Set(['ICS_Personal']);

/** Sub-types that require supervisor/manager approval on CIF add. */
export const SUPERVISOR_APPROVAL_SUBTYPES: ReadonlySet<string> = new Set([
  'ICS_Personal',
]);

/** Sub-types that surface the Finance-approval gate banner. */
export const FINANCE_APPROVAL_SUBTYPES: ReadonlySet<string> = new Set([
  'PublicFunds',
  'ICS_Personal',
]);

// -----------------------------------------------------------------------------
// Donation variant (short-term vs long-term)
// -----------------------------------------------------------------------------
// TODO(bill): government walkthrough needed — confirm whether this should
// be a separate CustomerType enum value (`DonationShortTerm` /
// `DonationLongTerm`) or remain a `variantKey` on person.extra. See
// government.md §Open Questions #3.

export type DonationVariant = 'ShortTerm' | 'LongTerm';

export interface DonationVariantOption {
  readonly value: DonationVariant;
  readonly label: string;
  readonly helpText: string;
  readonly tinCode: 'S' | 'E';
  readonly callClass: string;
}

export const DONATION_VARIANTS: ReadonlyArray<DonationVariantOption> = [
  {
    value: 'ShortTerm',
    label: 'Short-term Fundraiser',
    helpText:
      'Individual SSN-based. "Donation account for [beneficiary]" titling. ' +
      'Standard individual CDD applies.',
    tinCode: 'S',
    callClass: '32',
  },
  {
    value: 'LongTerm',
    label: 'Long-term Scholarship (Trust-treated)',
    helpText:
      'Requires Trust Agreement + Trust Certificate. CDD-exempt via reason 17.',
    tinCode: 'E',
    callClass: '32',
  },
];

// -----------------------------------------------------------------------------
// Primary-ID docCode mapping — per sub-type (for auto-fulfillment of uploads)
// -----------------------------------------------------------------------------
// TODO(bill): government walkthrough needed — confirm canonical docCodes for
// each sub-type. These are best-effort from government.md §"Document
// requirements" + pending seed SQL for `PublicFunds` / `Donation` /
// `ICS_Personal` in `scripts/seed/11-entity-rules.sql`.

export const PRIMARY_DOC_CODE_BY_SUBTYPE: Record<GovernmentSubType, string | undefined> = {
  PublicFunds: 'PUBFUNDS_BID_DOC',
  Donation: 'DONATION_STATEMENT',
  ICS_Personal: undefined,
};

/** Soft-required upload slot — Governing Body Resolution (PublicFunds only). */
export const GOVT_BANKING_RES_DOC_CODE = 'GOVT_BANKING_RES';

/** Hard-required upload slot — Finance Officer Authorization (PublicFunds + ICS_Personal). */
export const FINANCE_OFFICER_AUTH_DOC_CODE = 'FINANCE_OFFICER_AUTH';

// -----------------------------------------------------------------------------
// CIP Field 254 defaults (conservative per government.md §"Identity / CIP semantics")
// -----------------------------------------------------------------------------
// TODO(bill): government walkthrough needed — Field 254 value per sub-type
// is "to be defined during Government walkthrough" per people-form-port.md
// §13f. Defaults below are conservative best-effort:
//   PublicFunds: 8 (Other) — government enabling resolution is non-standard;
//                card suggests 6 (State Documents) but notes it's pending.
//                Conservative default is 8 per builder rules.
//   Donation:    0 (None) — short-term uses individual ID captured elsewhere;
//                long-term inherits Trust CIP.
//   ICS_Personal: not captured here (Finance-provided).

export const CIP_FIELD_254_DEFAULTS: Record<GovernmentSubType, string> = {
  PublicFunds: '8',     // Other — conservative default; Bill to confirm 6 vs 8
  Donation: '0',        // None — individual ID captured via separate signer Individual dialog
  ICS_Personal: '0',    // N/A — Finance-provided, no FSR CIP capture
};

// -----------------------------------------------------------------------------
// Helper text constants
// -----------------------------------------------------------------------------

export const ENTITY_NAME_HELPER_TEXT =
  'Legal government-entity name as filed (e.g., "City of Rolla", ' +
  '"Phelps County R-III School District"). Stored in Core Director Fields 6, 7.';

export const FINANCE_OFFICER_HELPER_TEXT =
  'Authorized Finance Officer / Treasurer of record for this government entity. ' +
  'Name + title must match the Governing Body Resolution.';

export const EIN_HELPER_TEXT =
  'Government entity Federal EIN (9 digits, no dashes). Core Director Field 13.';
